import React, { useState } from "react";
import { Box, Grid, Typography } from "@mui/material";
import CollapsibleSection from "../../pages/common-features/CollapsibleSection";
import DetailRow from "../../pages/common-features/DetailRow";
import SummarySectionHeader from "./SummarySectionHeader";

interface ReferenceField {
  label: string;
  name: string;
  value: string | number;
}

interface ReferenceDetailsProps {
  referenceDetails: Array<{
    [key: string]: ReferenceField[];
  }>;
  isMobile: boolean;
}

// Skeleton loader for reference details grid
export const referenceDetailsSkeleton = () =>
  Array.from({ length: 2 }).map((_, idx) => (
    <Grid item xs={12} key={`reference-skeleton-${idx}`}>
      <Box sx={{ mb: 1 }}>
        <Box
          sx={{
            width: "100%",
            height: 75,
            bgcolor: "#eee",
            borderRadius: 1,
            mb: 1,
          }}
        />
      </Box>
    </Grid>
  ));

const ReferenceDetailsTab: React.FC<ReferenceDetailsProps> = ({
  referenceDetails,
  isMobile,
}) => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  if (!referenceDetails || referenceDetails.length === 0)
    return (
      <Grid item xs={12}>
        <Typography variant="body2" color="textSecondary">
          No details available.
        </Typography>
      </Grid>
    );

  const basicFieldOrder = [
    "FirstName",
    "LastName",
    "Email",
    "EmailAddress",
    "PhoneNumber",
    "Mobile",
  ];
  const addressFieldOrder = [
    "AddressLine1",
    "AddressLine2",
    "AddressLine3",
    "Locality",
    "Landmark",
    "Area",
    "Pincode",
    "City",
    "State",
    "Country",
  ];

  const getSortedFields = (fields: ReferenceField[]) => {
    if (!fields) return [];
    return [...fields].sort((a, b) => {
      const orderFor = (field: ReferenceField) => {
        const name = String(field?.name || "");
        const basicMatch = name.match(/^basicReference\d+(.+)$/i);
        if (basicMatch) return basicFieldOrder.indexOf(basicMatch[1]);
        const addressMatch = name.match(/^addressReference\d+(.+)$/i);
        if (addressMatch) return addressFieldOrder.indexOf(addressMatch[1]);
        const partnerMatch = name.match(/^reference\d+(.+)$/i);
        if (partnerMatch) {
          const basicIndex = basicFieldOrder.indexOf(partnerMatch[1]);
          if (basicIndex !== -1) return basicIndex;
          const addressIndex = addressFieldOrder.indexOf(partnerMatch[1]);
          return addressIndex === -1 ? -1 : basicFieldOrder.length + addressIndex;
        }
        return -1;
      };
      const indexA = orderFor(a);
      const indexB = orderFor(b);
      if (indexA === -1 && indexB === -1) return 0;
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });
  };

  const getReferenceFieldLabel = (field: ReferenceField) => {
    const name = String(field?.name || "");
    const match =
      name.match(/^basicReference\d+(.+)$/i) ||
      name.match(/^addressReference\d+(.+)$/i) ||
      name.match(/^reference\d+(.+)$/i);
    if (!match) return field.label;
    return match[1]
      .replace(/([A-Z])/g, " $1")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/^./, (str) => str.toUpperCase());
  };

  return (
    <Grid item xs={12}>
      {referenceDetails?.map((refSection, idx) => {
        const refKey = Object.keys(refSection)[0];
        const rawFields = refSection[refKey];
        const sortedFields = getSortedFields(rawFields);
        const isExpanded = expandedIndex === idx;

        const basicFields = sortedFields.filter((f) => {
          const name = String(f?.name || "");
          const partnerMatch = name.match(/^reference\d+(.+)$/i);
          return (
            /^basicReference\d+/i.test(name) ||
            Boolean(partnerMatch && basicFieldOrder.includes(partnerMatch[1]))
          );
        });
        const addressFields = sortedFields.filter((f) => {
          const name = String(f?.name || "");
          const partnerMatch = name.match(/^reference\d+(.+)$/i);
          return (
            /^addressReference\d+/i.test(name) ||
            Boolean(partnerMatch && addressFieldOrder.includes(partnerMatch[1]))
          );
        });

        return (
          <Box key={refKey}>
            <CollapsibleSection
              title={`Reference ${idx + 1}`}
              isExpanded={isExpanded}
              onToggle={() => setExpandedIndex(isExpanded ? null : idx)}
            >
              {/* basic details */}
              {basicFields.length > 0 && (
                <Box sx={{ mb: 5 }}>
                  <Grid item xs={12} sx={{ mb: 2 }}>
                    <SummarySectionHeader title="Basic Details" />
                  </Grid>
                  <Grid container spacing={2} sx={{ ml: isMobile ? 0.2 : -2, width: isMobile ? 'auto' : 'calc(100% + 16px)' }}>
                    {basicFields.map((field) => (
                      <Grid item xs={12} sm={6} md={3} key={field.name}>
                        <DetailRow
                          item={{ ...field, label: getReferenceFieldLabel(field) }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* address details */}
              {addressFields.length > 0 && (
                <Box>
                  <Grid item xs={12} sx={{ mb: 2 }}>
                    <SummarySectionHeader title="Address Details" />
                  </Grid>
                  <Grid container spacing={2} sx={{ ml: isMobile ? 0.2 : -2, width: isMobile ? 'auto' : 'calc(100% + 16px)' }}>
                    {addressFields.map((field) => (
                      <Grid item xs={12} sm={6} md={3} key={field.name}>
                        <DetailRow
                          item={{ ...field, label: getReferenceFieldLabel(field) }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </CollapsibleSection>
          </Box>
        );
      })}
    </Grid>
  );
};

export default ReferenceDetailsTab;
